import React, { useEffect, useMemo, useState } from 'react';
/**
 * VerbEndingRenderer — SWW specialist werkwoord-uitgang cloze (dictation-grade).
 *
 * The dictation sister of SpellingPickRenderer. Each verb is shown in context
 * with the STEM visible and a highlighted gap immediately after it
 * (subject? + before + stem + [ending gap] + after). The learner taps the
 * correct ending tile (e.g. ∅ / 't' / 'dt' / 'te' / 'de') for each verb.
 *
 * The learner must APPLY the ending rule (PRODUCE the ending) rather than
 * recognise a fully conjugated word from MC options — productive yet fast and
 * playful (tap, not type), mapping 1-to-1 onto the classic -d/-t/-dt and
 * kofschip dictation skill.
 *
 * Contract (QuestionRendererProps):
 *  - reports ready via onReadyChange once every gap has an ending chosen
 *  - emits the answer (JSON array of chosen endings, verb-aligned) on the
 *    rising edge of shouldValidate
 */
import { motion, AnimatePresence } from 'framer-motion';
import { QuestionRendererProps } from './types';
import { FALLBACK_COLOR, hexToRgba } from './rendererUtils';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
import { resolveVerbEndings, type VerbEndingConfig } from '../../../content/types';
import { soundManager } from '../../../utils/soundManager';
import { hapticManager } from '../../../utils/hapticManager';
const VARIANT_LABEL: Record<VerbEndingConfig['variant'], string> = {
  'tt-tstam': 'Kies de juiste uitgang',
  'vt-zwak': 'te of de?',
  'vd-kof': 't of d?',
  custom: 'Kies de juiste uitgang'
};
/** Human-readable label for an ending tile. '' renders as a "no ending" chip. */
function endingTileLabel(ending: string): string {
  return ending === '' ? '—' : `-${ending}`;
}
export function VerbEndingRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  const config = question.data?.verbEnding as VerbEndingConfig | undefined;
  const items = config?.items ?? [];
  const endings = useMemo(() => config ? resolveVerbEndings(config) : [] as string[], [config]);
  // selections[itemIndex] = chosen ending (or null when still open).
  // We store nulls (not '') so the empty-string "no ending" is a real choice.
  const [selections, setSelections] = useState<(string | null)[]>(() => items.map(() => null));
  // Reset when the question changes.
  useEffect(() => {
    setSelections(items.map(() => null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id]);
  const allFilled = items.length > 0 && selections.every(s => s !== null && s !== undefined);
  useEffect(() => {
    onReadyChange?.(allFilled);
  }, [allFilled, onReadyChange]);
  // Edge-triggered submit — payload is a verb-aligned JSON array of endings.
  const answerPayload = useMemo(() => JSON.stringify(selections.map(s => s ?? '')), [selections]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  const handlePick = (itemIndex: number, ending: string) => {
    setSelections(prev => {
      const next = [...prev];
      next[itemIndex] = ending;
      return next;
    });
    soundManager.playClick();
    hapticManager.hapticSelection();
  };
  if (!config || items.length === 0 || endings.length === 0) {
    return <div className="p-4 bg-amber-50 rounded-xl text-amber-800 font-semibold text-center" data-id="element-11385">
        Deze vraag mist gegevens om te tonen.
      </div>;
  }
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3
  }} className="w-full max-w-xl mx-auto" data-id="element-11386">
      {/* Variant chip — instant "what am I doing" cue (P6). */}
      <div className="flex justify-center mb-5" data-id="element-11387">
        <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-bold tracking-wide" style={{
        backgroundColor: hexToRgba(color, 0.12),
        color
      }} data-id="element-11388">
          {config.promptLabel || VARIANT_LABEL[config.variant]}
        </span>
      </div>

      <ul className="flex flex-col gap-3" role="list" data-id="element-11389">
        {items.map((item, ii) => {
        const chosen = selections[ii];
        const hasChoice = chosen !== null && chosen !== undefined;
        return <li key={`${question.id}-v${ii}`} className="flex flex-col sm:flex-row sm:items-center gap-3 bg-white border-2 border-slate-200 rounded-2xl px-4 py-3 shadow-sm" data-id="element-11390">
              {/* The verb in context with its ending gap */}
              <div className="flex-1 flex items-center justify-center sm:justify-start flex-wrap gap-x-1.5 text-2xl sm:text-3xl font-extrabold text-slate-800 select-none" data-id="element-11391">
                {item.subject && <span className="text-slate-400 font-bold text-xl sm:text-2xl" data-id="element-11392">
                    {item.subject}
                  </span>}
                <span className="inline-flex items-baseline" data-id="element-11393">
                  {item.before && <span data-id="element-11394">{item.before}</span>}
                  <span data-id="element-11395">{item.stem}</span>
                  <AnimatePresence mode="wait" initial={false} data-id="element-11396">
                    {hasChoice ? <motion.span key={`filled-${chosen}`} initial={{
                  scale: 0.6,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} exit={{
                  scale: 0.6,
                  opacity: 0
                }} transition={{
                  type: 'spring',
                  stiffness: 420,
                  damping: 24
                }} className="px-0.5 rounded-md" style={{
                  color,
                  backgroundColor: hexToRgba(color, 0.1)
                }} data-id="element-11397">
                        {chosen}
                      </motion.span> : <motion.span key="gap" initial={{
                  opacity: 0.5
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} className="inline-block mx-1 align-middle" data-id="element-11398">
                        <span className="inline-block w-8 sm:w-10 border-b-4 border-dashed rounded-sm" style={{
                    borderColor: hexToRgba(color, 0.5),
                    height: '0.15em'
                  }} aria-hidden="true" data-id="element-11399" />
                      </motion.span>}
                  </AnimatePresence>
                  {item.after && <span data-id="element-11400">{item.after}</span>}
                </span>
              </div>

              {/* Ending tiles for this verb */}
              <div className="flex items-center justify-center gap-2 flex-wrap" role="group" aria-label={`Kies de uitgang voor werkwoord ${ii + 1}`} data-id="element-11401">
                {endings.map(ending => {
              const isSelected = chosen === ending;
              const label = endingTileLabel(ending);
              return <motion.button key={ending === '' ? '__none__' : ending} type="button" onClick={() => handlePick(ii, ending)} whileTap={{
                scale: 0.9
              }} whileHover={{
                scale: 1.05
              }} aria-pressed={isSelected} aria-label={ending === '' ? 'Geen uitgang (alleen de stam)' : label} className="min-w-[3rem] px-3 py-2 rounded-xl text-xl font-extrabold border-2 transition-colors" style={isSelected ? {
                backgroundColor: color,
                borderColor: color,
                color: '#fff'
              } : {
                backgroundColor: '#fff',
                borderColor: '#e2e8f0',
                color: '#334155'
              }} data-id="element-11402">
                      {label}
                    </motion.button>;
            })}
              </div>
            </li>;
      })}
      </ul>

      <p className="text-center text-slate-400 text-sm mt-6 font-medium" data-id="element-11403">
        {allFilled ? 'Klaar? Klik op Controleren!' : items.length > 1 ? 'Tik bij elk werkwoord de juiste uitgang aan' : 'Tik de juiste uitgang aan'}
      </p>
    </motion.div>;
}
VerbEndingRenderer.displayName = 'VerbEndingRenderer';